import { FeatureCollection, Point } from 'geojson'
import { AnyLayer, FillLayer, LineLayer, SymbolLayer } from 'mapbox-gl'

import { City } from '@/model/City'
import { TimeStep } from '@/model/TimeStep'
import { IMapService } from '@/services/map/IMapService'
import { MapBoxGL } from '../MapBoxGL'
import { IController } from './IController'
import { BaseRepresentationController } from './BaseRepresentationController'
import { IControllerParent } from './IControllerParent'
import { regularRepresentations, Representation } from '@/model/Representation'

const DEFAULT_SOURCE = 'rainfall'

export class RainfallController
  extends BaseRepresentationController<Representation<'rainfall'>>
  implements IController
{
  constructor(parent: IControllerParent, mapBoxGL: MapBoxGL, mapService: IMapService) {
    super(parent, mapBoxGL, mapService, regularRepresentations.rainfall)
  }

  async load(city: City, timeStep?: TimeStep, zoomOnSource = false, scale = 500): Promise<boolean> {
    let result = true

    if (!timeStep) {
      return Promise.resolve(false)
    }

    const requestedTimestamp = timeStep.timestamp.toISO()

    let geoJson = (await this.mapService?.fetchRainfall(city, timeStep)) as FeatureCollection
    if (!geoJson) {
      geoJson = this.emptyGeoJson
      result = false
    }

    const currentTimeStep = this.parent.getCurrentTimeStep()
    if (!timeStep.isMax() && currentTimeStep && requestedTimestamp !== currentTimeStep.timestamp.toISO()) {
      return result
    }

    this.mapBoxGL.showOnlyLayers([
      ...this.getLayers().map((layer) => layer.id),
      `${this.mapBoxGL.applicationPrefix}-raingaugestext`,
      `${this.mapBoxGL.applicationPrefix}-selectedraingauges`,
      `${this.mapBoxGL.applicationPrefix}-unselectedraingauges`,
      `${this.mapBoxGL.applicationPrefix}-cityContours*`,
    ])

    this.mapBoxGL.updateSource(DEFAULT_SOURCE, geoJson)
    if (zoomOnSource) {
      this.mapBoxGL.zoom(geoJson)
    }

    return result
  }

  getSources(): string[] {
    return [DEFAULT_SOURCE]
  }

  getLayers(): AnyLayer[] {
    const propertyName = this.representation.dynamicPropertyName
    return [
      {
        id: `${this.mapBoxGL.applicationPrefix}-rainfall`,
        type: 'fill',
        source: DEFAULT_SOURCE,
        minzoom: 7,
        paint: {
          'fill-opacity': 0.8,
          'fill-color': [
            'case',
            ['<', ['get', propertyName], this.representation.legend.thresholds[0].value],
            'transparent',
            ['interpolate', ['linear'], ['get', propertyName], ...this.unpackThresholds(this.representation.legend)],
          ],
        },
      } as FillLayer,
    ]
  }

  getLayersWithPopup(): AnyLayer[] {
    return [this.layers[0]]
  }

  getCursors(): Map<string, string> {
    return new Map([[`${this.mapBoxGL.applicationPrefix}-rainfall`, 'pointer']])
  }
}
